/**
 * The node's pose in a program that reads it. Godot's MODEL_MATRIX is the node's, which a
 * billboard or `fixed_size` never moves. three draws a node billboard or `fixed_size` with a moved
 * `modelMatrix`, and the draw hooks write both maps between the two before each draw. A surface
 * billboard keeps three's own matrices in {@link DRAWN_MODEL} and {@link DRAWN_INSTANCE}.
 */
import * as THREE from 'three';
/**
 * three's `modelMatrix` before any surface billboard replaces it. Not the node's model under a node
 * billboard or `fixed_size`: a reader multiplies it by `godotModelFromObject`.
 */
export const DRAWN_MODEL = 'GODOT_DRAWN_MODEL';
/** three's `instanceMatrix` before any surface billboard folds it into the model. */
export const DRAWN_INSTANCE = 'GODOT_DRAWN_INSTANCE';
/**
 * The declarations, guarded, so a program that two patches give the pose declares it once.
 *
 * @canonical The node's pose in a program
 */
export const NODE_POSE_PARS = /* glsl */ `#ifndef GODOT_NODE_POSE
#define GODOT_NODE_POSE
uniform mat4 godotObjectFromModel;
uniform mat4 godotModelFromObject;
#define ${DRAWN_MODEL} modelMatrix
#define ${DRAWN_INSTANCE} instanceMatrix
#endif
`;
/**
 * Each material's pose uniforms. Created by the first program that reads them, written by
 * `alignNodePose` before each draw, and collected with the material.
 */
// eslint-disable-next-line no-restricted-syntax -- alignNodePose reads an entry without building one, and a perObject read always builds
const poses = new WeakMap();
/**
 * The pose uniforms of `material`, one pair across its programs and recompiles.
 *
 * @canonical The node's pose in a program
 */
export function nodePoseUniforms(material) {
    let pose = poses.get(material);
    if (!pose) {
        pose = {
            godotObjectFromModel: { value: new THREE.Matrix4() },
            godotModelFromObject: { value: new THREE.Matrix4() },
        };
        poses.set(material, pose);
    }
    return pose;
}
/**
 * Points `material`'s next draw at the node's pose: `nodeMatrixWorld` is the node's model as Godot
 * holds it, and `modelMatrix` the one three draws with. A material whose programs read no pose is
 * left alone.
 */
export function alignNodePose(material, nodeMatrixWorld, modelMatrix) {
    const pose = poses.get(material);
    if (!pose)
        return;
    if (modelMatrix.equals(nodeMatrixWorld)) {
        pose.godotObjectFromModel.value.identity();
        pose.godotModelFromObject.value.identity();
        return;
    }
    pose.godotObjectFromModel.value.copy(nodeMatrixWorld).invert().multiply(modelMatrix);
    pose.godotModelFromObject.value.copy(modelMatrix).invert().multiply(nodeMatrixWorld);
}
//# sourceMappingURL=nodePose.js.map